import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { RadioButton } from 'primereact/radiobutton';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { formatDecimal } from '../../bidAnalysisTemplate.logic';
import classes from '../../bidAnalysisTemplate.module.scss';
import { ContractPriceRankingItem } from '../tables/ContractPriceRankingTable';

interface BidPriceFillUpModalProps {
  visible: boolean;
  onHide: () => void;
  competitor: ContractPriceRankingItem | null;
  onPriceSet: (price: number, note: string) => void;
}

type ReductionType = 'percentage' | 'cents';

export const BidPriceFillUpModal: React.FC<BidPriceFillUpModalProps> = ({
  visible,
  onHide,
  competitor,
  onPriceSet,
}) => {
  const [reductionType, setReductionType] = useState<ReductionType>('percentage');
  const [reductionValue, setReductionValue] = useState<number | null>(null);
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);

  // Reset form when modal opens/closes or competitor changes
  useEffect(() => {
    if (visible && competitor) {
      setReductionType('percentage');
      setReductionValue(null);
      setCalculatedPrice(null);
    }
  }, [visible, competitor]);

  // Calculate price whenever reduction type or value changes
  useEffect(() => {
    if (!competitor || reductionValue === null || reductionValue === undefined) {
      setCalculatedPrice(null);
      return;
    }

    const competitorPrice = competitor.price;
    let calculated: number;

    if (reductionType === 'percentage') {
      // Price lower by X%: newPrice = competitorPrice * (1 - X/100)
      const percentage = Math.max(0, Math.min(100, reductionValue)); // Clamp between 0-100
      calculated = competitorPrice * (1 - percentage / 100);
    } else {
      // Price lower by X cents: newPrice = competitorPrice - (X / 100)
      // 1 cent = 0.01 dollar, so divide by 100 to convert cents to dollars
      const cents = Math.max(0, reductionValue); // Ensure non-negative
      const centsInDollars = cents / 100; // Convert cents to dollars
      calculated = Math.max(0, competitorPrice - centsInDollars); // Ensure price doesn't go negative
    }

    // Round to 2 decimal places
    calculated = Math.round(calculated * 100) / 100;
    setCalculatedPrice(calculated);
  }, [competitor, reductionType, reductionValue]);

  const handleApply = () => {
    if (calculatedPrice === null || calculatedPrice < 0) {
      toast.error('Please enter a valid reduction value', { autoClose: 5000 });
      return;
    }

    if (!competitor) {
      toast.error('Competitor information is missing', { autoClose: 5000 });
      return;
    }

    // Generate note text
    // For cents, convert to percentage: (cents / 100) / competitorPrice * 100
    const percentageReduction =
      reductionType === 'percentage'
        ? reductionValue || 0
        : ((reductionValue || 0) / 100 / competitor.price) * 100;

    const noteText = `Priced ${percentageReduction.toFixed(2)}% lower than ${competitor.contracter_name} with market share ${competitor.current_market_share} and price $${formatDecimal(competitor.price)} in ${competitor.price_type}`;

    // Call the callback to set price and add note
    onPriceSet(calculatedPrice, noteText);

    // Reset and close
    setReductionType('percentage');
    setReductionValue(null);
    setCalculatedPrice(null);
    onHide();
  };

  const handleCancel = () => {
    setReductionType('percentage');
    setReductionValue(null);
    setCalculatedPrice(null);
    onHide();
  };

  if (!competitor) {
    return null;
  }

  const competitorName = competitor.contracter_name || 'Unknown';
  const competitorPrice = competitor.price;
  const marketShare = competitor.current_market_share || 'N/A';
  const priceType = competitor.price_type || 'N/A';

  return (
    <Dialog
      header="Set Bid Price Against Competitor"
      visible={visible}
      onHide={handleCancel}
      style={{ width: '500px' }}
      className={classes.bidPriceFillUpModal}
      blockScroll
    >
      <div style={{ padding: '1rem' }}>
        {/* Competitor Information */}
        <div
          style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#f4f6f9',
            borderRadius: '6px',
          }}
        >
          <h4
            style={{ margin: '0 0 0.75rem 0', color: '#522d6e', fontSize: '1rem', fontWeight: 600 }}
          >
            Competitor Information
          </h4>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '0.75rem',
              fontSize: '0.875rem',
            }}
          >
            <div>
              <span style={{ color: '#6b7280', fontWeight: 500 }}>Competitor:</span>
              <div style={{ color: '#1f2937', marginTop: '0.25rem' }}>{competitorName}</div>
            </div>
            <div>
              <span style={{ color: '#6b7280', fontWeight: 500 }}>Price:</span>
              <div style={{ color: '#1f2937', marginTop: '0.25rem', fontWeight: 600 }}>
                ${formatDecimal(competitorPrice)}
              </div>
            </div>
            <div>
              <span style={{ color: '#6b7280', fontWeight: 500 }}>Market Share:</span>
              <div style={{ color: '#1f2937', marginTop: '0.25rem' }}>{marketShare}</div>
            </div>
            <div>
              <span style={{ color: '#6b7280', fontWeight: 500 }}>Price Type:</span>
              <div style={{ color: '#1f2937', marginTop: '0.25rem' }}>{priceType}</div>
            </div>
          </div>
        </div>

        {/* Reduction Type Selection */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '0.75rem',
              color: '#374151',
              fontWeight: 500,
              fontSize: '0.875rem',
            }}
          >
            Reduction Type
          </label>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <RadioButton
                inputId="percentage"
                name="reductionType"
                value="percentage"
                checked={reductionType === 'percentage'}
                onChange={e => setReductionType(e.value)}
              />
              <label htmlFor="percentage" style={{ cursor: 'pointer', color: '#374151' }}>
                Percentage (%)
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <RadioButton
                inputId="cents"
                name="reductionType"
                value="cents"
                checked={reductionType === 'cents'}
                onChange={e => setReductionType(e.value)}
              />
              <label htmlFor="cents" style={{ cursor: 'pointer', color: '#374151' }}>
                Cents (c)
              </label>
            </div>
          </div>
        </div>

        {/* Reduction Value Input */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#374151',
              fontWeight: 500,
              fontSize: '0.875rem',
            }}
          >
            {reductionType === 'percentage' ? 'Percentage Reduction (%)' : 'Cents Reduction (c)'}
          </label>
          <InputNumber
            value={reductionValue}
            onValueChange={e => setReductionValue(e.value ?? null)}
            placeholder={reductionType === 'percentage' ? 'Enter percentage' : 'Enter cents'}
            mode="decimal"
            min={0}
            max={reductionType === 'percentage' ? 100 : undefined}
            minFractionDigits={reductionType === 'percentage' ? 2 : 0}
            maxFractionDigits={reductionType === 'percentage' ? 2 : 0}
            suffix={reductionType === 'percentage' ? '%' : 'c'}
            prefix={undefined}
            style={{ width: '100%' }}
            className="p-inputtext"
          />
        </div>

        {/* Calculated Price Display */}
        {calculatedPrice !== null && (
          <div
            style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: '#ecfdf5',
              borderRadius: '6px',
              border: '1px solid #10b981',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#374151', fontWeight: 500, fontSize: '0.875rem' }}>
                Suggested Bid Price:
              </span>
              <span style={{ color: '#10b981', fontWeight: 700, fontSize: '1.25rem' }}>
                ${formatDecimal(calculatedPrice)}
              </span>
            </div>
            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
              {reductionType === 'percentage' ? (
                <>
                  {competitorPrice.toFixed(2)} × (1 - {reductionValue ?? 0}%) = $
                  {formatDecimal(calculatedPrice)}
                </>
              ) : (
                <>
                  ${formatDecimal(competitorPrice)} - ${formatDecimal((reductionValue ?? 0) / 100)}{' '}
                  = ${formatDecimal(calculatedPrice)}
                </>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            marginTop: '1.5rem',
          }}
        >
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={handleCancel}
            className="p-button-text"
            style={{ color: '#6b7280' }}
          />
          <Button
            label="Apply"
            icon="pi pi-check"
            onClick={handleApply}
            disabled={calculatedPrice === null || calculatedPrice < 0}
            style={{ backgroundColor: '#522d6e', borderColor: '#522d6e' }}
          />
        </div>
      </div>
    </Dialog>
  );
};
